import { type OrderStatus } from '@wms/shared';
import { formatDate, formatDateTime } from '../lib/format.js';

export interface OrderStageActor {
  displayName: string;
  email: string | null;
}

export interface OrderStageChange {
  isComment?: boolean;
  toStatus: OrderStatus;
  timestamp: string;
  actor: OrderStageActor | null;
}

export function orderStageTimestamp(
  value: string | null,
  changes: readonly OrderStageChange[],
  status: OrderStatus,
): string | null {
  return (
    value ??
    changes.find((change) => !change.isComment && change.toStatus === status)?.timestamp ??
    null
  );
}

export function orderStageActor(
  changes: readonly OrderStageChange[],
  status: OrderStatus,
): OrderStageActor | null {
  return changes.find((change) => !change.isComment && change.toStatus === status)?.actor ?? null;
}

export function orderStageActorLabel(actor: OrderStageActor | null): string | null {
  if (!actor) return null;
  return actor.displayName.trim() || actor.email;
}

export function OrderStageStamp({
  value,
  changes,
  status,
  withTime = false,
}: {
  value: string | null;
  changes: readonly OrderStageChange[];
  status: OrderStatus;
  withTime?: boolean;
}) {
  const timestamp = orderStageTimestamp(value, changes, status);
  const actorLabel = timestamp ? orderStageActorLabel(orderStageActor(changes, status)) : null;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span>{withTime ? formatDateTime(timestamp) : formatDate(timestamp)}</span>
      {actorLabel ? (
        <span className="text-[11px] text-muted-foreground" title={actorLabel}>
          {actorLabel}
        </span>
      ) : null}
    </span>
  );
}
